import { PaginatedData, PaginatedResponse } from "../../types";
import { fixDoubleEscapedStringResponse } from "../../utils/fixDoubleEscapedStringResponse";
import { getSchemaTypesWithFallback } from "../../utils/schemasFallback";
import { APIQuery } from "../../utils/sharedTypes";
import { sortConfigToQueryParam } from "../../utils/sortUtils";
import { ApiServiceCollection } from "../ApiServiceCollection.class";
import { ApiResponse } from "../RequestManager.class";
import { ApiServiceEndpoint } from "../api.types";
import { ATTRIBUTION_SCHEMA_TYPES } from "../mocks/MockDatabaseCRUD";
import { AttributionListAPI } from "../mocks/database/tables/attributions.mockTable";
import { Attribution, AttributionBaseDB, AttributionSummary, AttributionVersionsResponse, JsonSchemaType } from "./types";

export class AttributionService extends ApiServiceCollection {
    translateAttributionsFromServer = (attributions: AttributionListAPI[]) =>
        (attributions || []).map(({
            display_name,
            attribution_guid,
            status,
            type,

        }) => ({
            attributionGuid: attribution_guid,
            displayName: display_name,
            status: status,
            type: type,

        } as Attribution))

    translateSchemasFromServer = (schemas: JsonSchemaType[]) => {
        return getSchemaTypesWithFallback(schemas) as JsonSchemaType[]
    }

    getSchemasMockData = (type: 'attribution' | 'quote-experience') => {
        if (!this.mocked) {
            return [];
        }
        const schemas = this.mockCRUD!.getSchemasByUsageType(type);
        return {
            count: schemas.length,
            schemas
        }
    }

    getAttributions: ApiServiceEndpoint<PaginatedData<AttributionSummary>> = (
        { skip, limit, query }: { skip: number; limit: number, query?: APIQuery }
    ) => {
        const queryParams = this.endpointParamsToQueryParams({ skip, limit, query });
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/attribution?${queryParams}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            }
        }, {
            mock: this.mocked,
            mockData: () => {
                try {
                    const mockDataResp = this.getMockData({
                        tableName: 'attributions',
                        skip, limit, sort: sortConfigToQueryParam(query?.sort || null) || undefined,
                        queryParams: (query?.filter || {}),
                        paginated: true
                    });
                    const { data: paginatedResp, ...mockDataRem } = mockDataResp;
                    const { data: paginatedData, total } = paginatedResp;
                    return {
                        ...mockDataRem,
                        data: {
                            total,
                            data: paginatedData.filter((rec: Record<string, any>) => ATTRIBUTION_SCHEMA_TYPES.indexOf(rec['type']) > -1)
                        }
                    }
                } catch (e) {
                    console.error(`An unexpected error occurred: ${e}`);
                    return {
                        data: { error: `An unexpected error occurred` },
                        status: 500,
                        error: {
                            message: `An unexpected error occurred`,
                            code: 'INTERNAL_SERVER'
                        }
                    }
                }
            },
            getMockStatus: () => this.getNextMockStatus('AttributionService.getAttributions'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as PaginatedResponse<AttributionListAPI>;
                return {
                    response: response as Response,
                    success: true,
                    jsonBody: {
                        data: this.translateAttributionsFromServer(respData.attributions || respData.data || []),
                        total: respData.total
                    }
                } as ApiResponse<PaginatedData<AttributionSummary>>
            }
        })
        .catch(error => error)
    }

    private transformSchemasResponse = async (apiResponse: ApiResponse) => {
        const response = apiResponse.response;
        const respData = await response?.json();
        return {
            success: true,
            response,
            jsonBody: {
                schemas: this.translateSchemasFromServer(Array.isArray(respData) ? respData : respData.schemas)
            }
        };
    };

    getAttributionSchemas: ApiServiceEndpoint<{
        schemas: JsonSchemaType[]
    }> = () => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/attribution/schema`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: {
                data: this.getSchemasMockData('attribution'),
                status: 200
            },
            getMockStatus: () => this.getNextMockStatus('AttributionService.getAttributionSchemas'),
            transformResponsePayload: this.transformSchemasResponse
        })
            .catch(error => error)
    };

    getAttributionSchemasByType: ApiServiceEndpoint<{
        schemas: JsonSchemaType[]
    }> = (params: { type: 'attribution' | 'quote-experience' }) => {
        return this.standardAuthRequest(
            `${process.env.REACT_APP_MANAGEMENT_API_URL}/attribution/schema?type=${encodeURIComponent(params.type)}`,
            {
                method: 'GET',
                headers: {
                    ...this.getJSONHeaders()
                },
            }, {
            mock: this.mocked,
            mockData: {
                data: this.getSchemasMockData(params.type),
                status: 200
            },
            getMockStatus: () => this.getNextMockStatus('AttributionService.getAttributionSchemas'),
            transformResponsePayload: this.transformSchemasResponse
        }
        )
            .catch(error => error)
    };

    getAttributionById: ApiServiceEndpoint<Attribution> = (attributionGuid: string) => {
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/attribution/${attributionGuid}`, {
            method: 'GET',
            headers: {
                ...this.getJSONHeaders()
            },
        }, {
            mock: this.mocked,
            mockData: this.getMockDataSingular({
                tableName: 'attributions',
                queryParams: new URLSearchParams({ attribution_guid: attributionGuid })
            }),
            getMockStatus: () => this.getNextMockStatus('AttributionService.getAttributionById'),
            transformResponsePayload: async (apiResponse: ApiResponse) => {
                const response = apiResponse.response;
                const respData = await response?.json() as AttributionBaseDB;
                return {
                    success: true,
                    response,
                    jsonBody: {
                        attributionGuid: respData.attribution_guid,
                        displayName: respData.display_name,
                        status: respData.status,
                        type: respData.type,
                        properties: respData.properties,
                        typeVersion: respData.type_version,
                        propertiesVersion: respData.properties_version,
                    } as Attribution
                }
            }
        })
            .catch(error => error);
    }

    createAttribution: ApiServiceEndpoint<string> = (
        data: Omit<Attribution, "attributionGuid">
    ) => {
        let response: Response | null = null;
        const requestBody: Record<string, any> = {
            "display_name": data.displayName,
            "status": data.status,
            "type": data.type,
            "properties": data.properties
        };
        if (data.typeVersion) {
            requestBody['type_version'] = data.typeVersion;
        }
        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL as string}/attribution`, {
            method: 'POST',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(requestBody)
        }, {
            mock: this.mocked,
            mockData: () => this.mockCRUD!.createAttribution(data),
            getMockStatus: () => this.getNextMockStatus('AttributionService.createAttribution')
        })
            .then(resp => {
                response = resp.response!;
                return resp.response!.text()
            })
            .then(respData => {
                console.log(`Attribution created. Response: ${respData}`);
                return {
                    success: true,
                    response: response as Response,
                    textBody: fixDoubleEscapedStringResponse(respData) as string
                }
            })
            .catch(error => error)
    }

    updateAttribution: ApiServiceEndpoint<void> = (data: Omit<Attribution, "type">) => {
        const { attributionGuid, displayName, status, typeVersion, propertiesVersion, schemaType, ...remainingData } = data;
        const updateAttributionData = remainingData as Record<string, any>

        if (displayName) {
            updateAttributionData['display_name'] = displayName;
        }
        if (typeVersion) {
            updateAttributionData['type_version'] = typeVersion;
        }

        return this.standardAuthRequest(`${process.env.REACT_APP_MANAGEMENT_API_URL}/attribution/${attributionGuid}`, {
            method: 'PATCH',
            headers: {
                ...this.getJSONHeaders()
            },
            body: JSON.stringify(updateAttributionData)
        }, {
            mock: this.mocked,
            mockData: () => this.mockCRUD!.updateAttribution({ attributionGuid, updateAttributionData }),
            getMockStatus: () => this.getNextMockStatus('AttributionService.updateAttribution')
        })
            .then(response => response)
            .catch(error => error);
    }

    getAttributionVersions: ApiServiceEndpoint<AttributionVersionsResponse> = (attributionGuid: string) => {
        return this.standardAuthRequest(
            `${process.env.REACT_APP_MANAGEMENT_API_URL}/attribution/${attributionGuid}/versions`,
            {
                method: 'GET',
                headers: {
                    ...this.getJSONHeaders()
                },
            },
            {
                mock: this.mocked,
                mockData: () => {
                    try {
                        let schema_data_id: string | null = null;
                        // Could turn up missing on reload. Use query instead of one
                        const attrRec = this.mockCRUD!.db().getTableByName('attributions').query([{
                            fieldName: 'attribution_guid',
                            value: attributionGuid
                        }]);
                        if (attrRec.length) {
                            schema_data_id = attrRec[0].values['schema_data_id'];
                        }
                        if (!schema_data_id) {
                            return {
                                data: null,
                                status: 500,
                                error: {
                                    message: 'Failed to access attribution versions. Bad data.',
                                    code: 'INTERNAL_SERVER'
                                }
                            }
                        }
                        return this.getMockData({
                            tableName: 'schema_data',
                            queryParams: {
                                schema_data_id: schema_data_id!,
                                skip: '0',
                                limit: '1000'
                            },
                            paginated: true
                        });
                    } catch (e) {
                        return {
                            data: { error: `An unexpected error occurred` },
                            status: 500,
                            error: {
                                message: `An unexpected error occurred`,
                                code: 'INTERNAL_SERVER'
                            }
                        }
                    }
                },
                getMockStatus: () => this.getNextMockStatus('AttributionService.getAttributionVersions'),
                transformResponsePayload: async (apiResponse: ApiResponse) => {
                    const response = apiResponse.response;
                    const { data, versions, total } = await response?.json() as AttributionVersionsResponse;
                    return {
                        success: true,
                        response,
                        jsonBody: {
                            data: data || versions,
                            total
                        }
                    }
                }
            }
        )
            .catch(error => error);
    }
}